import * as React from 'react';

import { connect } from 'react-redux';

import moment from 'moment';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { joinList } from '@webapp/utils/normalizers';

import { Release } from '@common/types/release/release';
import { getPreorderDate } from '@webapp/utils/release/getDate';
import { trackList } from '@webapp/store/tracks/selectors';
import SimpleTrack from '@common/types/release/simple-track';

// Store
import AppState from '@webapp/store/state';
import { collaboratorsList } from '@webapp/store/collaborator/selectors';
import { contributorsList } from '@webapp/store/collaborator/selectors';
import { ownersList, writersList } from '@webapp/store/ownership/selectors';
import { CollaboratorState } from '@webapp/store/collaborator/state';
import { OwnershipState } from '@webapp/store/ownership/state';
import Typography from '@components/basics/typography';

// Components
import PreviewHint from './preview-hint';
import SongInfoRow from './song-info-row';
import Hint from '@webapp/components/basics/hint';
import PreviewBox from '@webapp/components/layout/preview-box';

interface ReleasePreviewCtrlProps {
  className?: string;
}

interface MappedProps {
  release?: Partial<Release>;
  storesLength: number;
  tracks: Partial<SimpleTrack>[];
  collaborators: CollaboratorState;
  owners: OwnershipState;
  isMobile: boolean;
  isLaptop: boolean;
}

const getFirstReleaseDate = (release?: Partial<Release>) => {
  const date =
    release?.releaseHistory === 'new'
      ? release?.releaseDate
      : release?.previousReleaseDate;

  if (!date) {
    return '';
  }

  return moment(date).format('LL');
};

const mapStateToProps = (state: AppState) => {
  const isBeginner =
    state.subscription.current?.plan?.toLocaleLowerCase() === 'beginner';
  return {
    release: state.release.release,
    tracks: trackList(state.tracks.tracks, state.release.release?.id),
    storesLength: (state.release.release?.stores || []).filter((s) => {
      if (isBeginner) {
        const found = (state.categories.lists.platform || []).find(
          (c) => c.metadata.id === s.id
        );
        if (found && found.paid) {
          return false;
        }
      }
      return true;
    }).length,
    collaborators: state.collaborator,
    owners: state.ownership,
    isMobile: state.ui.mobile,
    isLaptop: state.ui.laptop,
  };
};

export const ReleasePreviewCtrl: React.FC<
  ReleasePreviewCtrlProps & MappedProps
> = (props) => {
  return (
    <div className={props.className}>
      <PreviewBox
        isMobile={props.isMobile}
        srcImg={props.release?.cover?.url}
        songName={props.release?.title || ''}
        artists={
          props.release?.artists
            ?.filter((a) => ['main', 'owner'].includes(a.role))
            .map((a) => {
              return { name: a.name, avatar: a?.avatar ?? '' };
            }) || []
        }
        featuring={
          props.release?.artists
            ?.filter((a) => a.role === 'featuring')
            .map((a) => {
              return { name: a.name, avatar: a?.avatar ?? '' };
            }) || []
        }
        label={props.release?.recordLabel || ''}
        stores={props.release?.storesBrief || ''}
        storesLength={props.storesLength}
        regionsLength={props.release?.regions?.length ?? 0}
        genre={props.release?.genres || []}
        date={getFirstReleaseDate(props.release)}
        price={props.release?.albumPrice}
        preorderDate={getPreorderDate(props.release)}
        hasYoutubeContentID={!!props.release?.hasYoutubeContentID}
        isStoreMaximizer={!!props?.release?.isStoreMaximizer}
      />

      {props.isLaptop && <PreviewHint isMobile />}

      {!props.isMobile ? (
        <ColumnHeader>
          <div className="track-number">
            <Typography weight="700">#</Typography>
          </div>
          <div className="song-name">
            <Typography weight="700">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.HEADER.TRACK_NAME" />
            </Typography>
          </div>
          <div className="feat">
            <CircleWrapper>
              <Typography weight="700" type="smaller">
                Co
              </Typography>
            </CircleWrapper>
          </div>
          <div className="composer">
            <CircleWrapper>
              <Typography weight="700" type="smaller">
                So
              </Typography>
            </CircleWrapper>
          </div>
          <div className="owners">
            <CircleWrapper>
              <Typography weight="700" type="smaller">
                Mo
              </Typography>
            </CircleWrapper>
          </div>
          <div className="credits">
            <CircleWrapper>
              <Typography weight="700" type="smaller">
                Cr
              </Typography>
            </CircleWrapper>
          </div>
        </ColumnHeader>
      ) : (
        <ColumnHeader>
          <div className="track-number">
            <Typography weight="700">#</Typography>
          </div>
          <div className="song-name">
            <Typography weight="700">
              <Translate id="RELEASE.PREVIEW.SONG_LIST.HEADER.TRACK_NAME" />
            </Typography>
          </div>
        </ColumnHeader>
      )}

      <Wrapper>
        {props.tracks.map((t) => (
          <SongInfoRow
            key={t.id as string}
            track={t}
            number={t.order || 0}
            songName={t.title as string}
            isClean={t.isClean}
            isExplicit={t.isExplicit}
            isInstrumental={t.type === 'instrumental'}
            isInstantGratification={t.isInstantGratification}
            // TODO: DEVTEAM-958 - NFT - This need to be integrated to the track.
            // isNFTExlusive={!!track.isNFTExlusive}
            hasLyrics={!!t.lyrics}
            isActive={true}
            language={t?.language ?? ''}
            lyrics={!!t?.lyrics}
            artist={collaboratorsList(
              props.collaborators,
              t.id as string
            ).filter((a) => a.role === 'main')}
            artistArray={collaboratorsList(props.collaborators, t.id as string)
              .filter((a) => a.role === 'main')
              .map((a) => {
                return {
                  name: a.name || a.contact || '',
                  avatar: a?.avatar || '',
                };
              })}
            featuring={joinList(
              collaboratorsList(props.collaborators, t.id as string)
                .filter((a) => a.role === 'featuring')
                .map((a) => a.name || a.contact || '')
            )}
            featuringArray={collaboratorsList(
              props.collaborators,
              t.id as string
            )
              .filter((a) => a.role === 'featuring')
              .map((a) => {
                return {
                  name: a.name || a.contact || '',
                  avatar: a?.avatar || '',
                };
              })}
            composerArray={writersList(props.owners, t.id as string)}
            owners={ownersList(props.owners, t.id as string)}
            hasCredits={
              !!contributorsList(props.collaborators, t.id as string).length
            }
            credits={contributorsList(props.collaborators, t.id as string).map(
              (a) => {
                return {
                  name: a.name || a.contact || '',
                  avatar: a?.avatar ?? '',
                };
              }
            )}
          />
        ))}
        {props.release?.title?.toLowerCase()?.includes('feat.') ||
        props.release?.title?.toLowerCase()?.includes('prod. by') ? (
          <Hint noBorderTop noTitle align="left">
            <Translate id="RELEASE.PREVIEW.HINT.INFO.TEXT" />
          </Hint>
        ) : null}
      </Wrapper>
    </div>
  );
};

export default connect(mapStateToProps)(ReleasePreviewCtrl);

const ColumnHeader = styled.div`
  display: grid;
  grid-gap: 15px;
  align-items: center;
  grid-template-columns: repeat(9, minmax(0, 1fr));
  padding: 20px;
  margin-top: 10px;

  @media ${({ theme }) => theme.device.tablet} {
    padding: 20px 30px;
  }

  .track-number {
    margin: 0 auto;
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .song-name {
    grid-column: 2 / 9;
    @media ${({ theme }) => theme.device.tablet} {
      grid-column: 2 / 6;
    }
  }
`;

const CircleWrapper = styled.div`
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  width: 24px;
  height: 24px;
`;

const Wrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;
